import React from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  Background,
  BackgroundVariant,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

// ReactFlow blocks wheel-zoom and panning when the wheel/pan event's target sits inside an element
// carrying its noWheelClassName / noPanClassName (default 'nowheel' / 'nopan'). The playground wraps
// every widget body in a `.nowheel` element so scrolling over a widget never zooms the OUTER playground
// canvas — but that `.nowheel` is also an ancestor of THIS nested canvas, so with the default class our
// own wheel-zoom would be suppressed too (the check is an unbounded `closest('.nowheel')`). Giving this
// canvas its own private no-wheel class decouples it: the outer `.nowheel` no longer matches here, so the
// mission graph zooms on wheel/pinch while the outer playground still ignores the same scroll. Node
// dragging is unaffected — that check is DOM-bounded to this canvas, so the wrapper's `nodrag` never
// reaches our nodes.
const NO_WHEEL_CLASS = 'mc-graph-nowheel';

/**
 * GraphCanvasInner — controlled ReactFlow canvas for Mission Control surfaces.
 * Intentionally simpler than the generic graph widget's canvas: no layout
 * computation, no subset mode, no delete-key interception. Surfaces that need
 * interaction (selection, drag-to-connect, node dragging) pass the optional handlers
 * below; absent props keep the canvas static, which is how the read-only workflows
 * surface uses it. The canvas defines the mission dependency-edge arrow markers
 * (mc-graph-edge-arrow / -selected) and accepts an optional `edgeTypes` map; the cells
 * surface passes a 'dependsOn' edge type that references those markers, while surfaces
 * that omit `edgeTypes` (e.g. workflows) fall back to ReactFlow's built-in edge types.
 * Node dragging is controlled per-node (node.draggable), not at the canvas level.
 *
 * The canvas always pans (drag the empty pane) and zooms (wheel + pinch); multi-select
 * is disabled (single selection only) so a background drag always pans instead of
 * rubber-band-selecting.
 *
 * Props:
 *   nodes            — ReactFlow node array
 *   edges            — ReactFlow edge array
 *   nodeTypes        — custom node type map (optional; falls back to ReactFlow defaults)
 *   edgeTypes        — custom edge type map (optional; falls back to ReactFlow defaults)
 *   connectionMode   — 'strict' (default) or 'loose'; 'loose' lets either of a cell's two handles
 *                      initiate a depends_on drag, which the mission graph relies on
 *   onNodeClick      — (node) => void
 *   onEdgeClick      — (edge) => void; fired when an edge is clicked (select)
 *   onPaneClick      — () => void; fired when the empty canvas is clicked (deselect)
 *   onConnect        — (connection) => void; when provided, nodesConnectable is enabled
 *   fitView          — boolean (default true)
 *   onNodesChange    — (changes) => void; forwarded to ReactFlow for controlled-node interaction
 *   onEdgesChange    — (changes) => void; forwarded to ReactFlow for controlled-edge interaction
 *   onNodeDragStop   — (node) => void; fired with the dragged node once a drag ends (persist position)
 */
function GraphCanvasInner({
  nodes, edges, nodeTypes, edgeTypes, connectionMode, onNodeClick, onEdgeClick,
  onPaneClick, onConnect, fitView, onNodesChange, onEdgesChange, onNodeDragStop,
}) {
  const handleNodeClick = (_event, node) => {
    if (onNodeClick) onNodeClick(node);
  };

  const handleEdgeClick = (_event, edge) => {
    if (onEdgeClick) onEdgeClick(edge);
  };

  const handleConnect = (connection) => {
    if (onConnect) onConnect(connection);
  };

  const handleNodeDragStop = (_event, node) => {
    if (onNodeDragStop) onNodeDragStop(node);
  };

  const connectable = !!onConnect;

  return (
    <div style={{ width: '100%', height: '100%' }}>
      {/* Filled arrowhead markers for the mission dependency edge; the selected variant recolours with
          the edge. Copied from the generic graph widget's canvas (widgets/graph/canvas.js) so the
          arrow matches it exactly — bigger than the default marker and clipped to touch the node. */}
      <svg style={{ position: 'absolute', width: 0, height: 0 }} aria-hidden="true">
        <defs>
          {[
            { id: 'mc-graph-edge-arrow', color: '#9aa3af' },
            { id: 'mc-graph-edge-arrow-selected', color: '#3d4756' },
          ].map(m => (
            <marker
              key={m.id}
              id={m.id}
              markerWidth="11.2"
              markerHeight="5.6"
              viewBox="0 0 16 8"
              orient="auto"
              refX="16"
              refY="4"
              markerUnits="userSpaceOnUse"
            >
              {/* Filled, elongated (≈2:1 long-to-wide) arrowhead. */}
              <path d="M0,0 L16,4 L0,8 Z" fill={m.color} />
            </marker>
          ))}
        </defs>
      </svg>
      <ReactFlow
        nodes={nodes || []}
        edges={edges || []}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        connectionMode={connectionMode}
        onNodeClick={handleNodeClick}
        onEdgeClick={onEdgeClick ? handleEdgeClick : undefined}
        onPaneClick={onPaneClick}
        onConnect={connectable ? handleConnect : undefined}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeDragStop={onNodeDragStop ? handleNodeDragStop : undefined}
        fitView={fitView !== false}
        fitViewOptions={{ maxZoom: 1.1, padding: 0.2 }}
        deleteKeyCode={null}
        nodesConnectable={connectable}
        panOnDrag
        zoomOnScroll
        zoomOnPinch
        noWheelClassName={NO_WHEEL_CLASS}
        multiSelectionKeyCode={null}
        selectionKeyCode={null}
        selectionOnDrag={false}
        attributionPosition="bottom-right"
      >
        <Controls showInteractive={false} />
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
      </ReactFlow>
    </div>
  );
}

/**
 * GraphCanvas — wraps the inner canvas in a ReactFlowProvider.
 */
function GraphCanvas(props) {
  return (
    <ReactFlowProvider>
      <GraphCanvasInner {...props} />
    </ReactFlowProvider>
  );
}

export default GraphCanvas;
